import AddIcon from '@mui/icons-material/Add';
import { Avatar, Box, Chip, Typography, useTheme, useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';
import React from 'react';
import Button from '../components/Button';
import IconButton from '../components/IconButton';
import { ICON_BUTTON_TYPE } from '../constants';
import MedicationIcon from '@mui/icons-material/Medication';
import { isEmpty, map } from 'lodash';
import { Assistant } from '@mui/icons-material';
import { medicationUserAnalyze } from '../utils/api';
import useStore from '../Store/Store';
import SmartMedCheckModal from './SmartMedCheckModal';

const MedicationCard = ({
  name,
  frequency,
  dose,
  times,
  howToTake,
  description,
  responsible,
  duration,
  handleDeleteMedications,
  color,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        py: 3,
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between',
          alignItems: isMobile ? 'flex-start' : 'center',
          gap: isMobile ? 2 : 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: color || '#26786a' }}>
            <MedicationIcon />
          </Avatar>
          <Typography variant="h6">{name}</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          {responsible && <Chip label="Responsible" avatar={<Avatar src="/avatar.png" />} />}
          <Typography variant="body2">
            {t('duration')}: {duration}
          </Typography>
          <IconButton type={ICON_BUTTON_TYPE.delete} onClick={handleDeleteMedications} />
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          gap: 4,
          mt: 2,
          flexDirection: isTablet ? 'column' : 'row',
          '& > *': {
            flex: isTablet ? '1 1 100%' : '1 1 auto',
          },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="body2">{t('frequency')}</Typography>
          <Typography variant="body1">{t(frequency)}</Typography>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="body2">{t('dose')}</Typography>
          <Typography variant="body1">{dose}</Typography>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="body2">{t('time')}</Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {times?.map((time, index) => (
              <Chip key={index} label={time} />
            ))}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="body2">{t('howToTake')}</Typography>
          <Typography variant="body1">{t(howToTake)}</Typography>
        </Box>
      </Box>
      <Typography variant="body2" sx={{ mt: 2 }}>
        {description}
      </Typography>
    </Box>
  );
};

const MedicationsList = ({ setShowMedicationTable, medications, loader, handleDeleteMedications }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const user = useStore((state) => state.user);
  const recipient = useStore((state) => state.selectedRecipient);
  const [showSmartMedModal, setShowSmartMedModal] = React.useState(false);
  const [analysisData, setAnalysisData] = React.useState(null);
  const [isAnalyzing, setIsAnalyzing] = React.useState(false);

  const handleSmartMedCheck = async () => {
    setIsAnalyzing(true);
    setShowSmartMedModal(true);
    setAnalysisData(null);

    try {
      const response = await medicationUserAnalyze({ userId: user?._id });
      if (response.success === true) {
        setAnalysisData(response.data);
      } else {
        setAnalysisData({ error: t('medication.failedToAnalyze') });
      }
    } catch (error) {
      console.error('Smart med check error:', error);
      setAnalysisData({ error: t('medication.analysisError') });
    } finally {
      setIsAnalyzing(false);
    }
  };
  const closeSmartMedModal = () => {
    setShowSmartMedModal(false);
    setAnalysisData(null);
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          height: isMobile ? 'auto' : '65vh',
          p: isMobile ? 2 : 3,
          bgcolor: 'background.paper',
          borderRadius: 2,
          overflow: 'scroll',
        }}
        className="hide-scroll"
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: 'space-between',
            alignItems: isMobile ? 'stretch' : 'center',
            gap: isMobile ? 2 : 0,
            mb: 3,
          }}
        >
          <Typography variant="h5">{t('medications')}</Typography>
          <div style={{ display: 'flex', gap: 10, flexDirection: isMobile ? 'column' : 'row' }}>
            <Button
              text={t('addMedications')}
              startIcon={<AddIcon />}
              onClick={setShowMedicationTable}
              fullWidth={isMobile}
            />
            {!recipient && !isEmpty(medications) && (
              <Button
                text={t('medication.smartMedCheck')}
                startIcon={<Assistant />}
                onClick={handleSmartMedCheck}
                fullWidth={isMobile}
              />
            )}
          </div>
        </Box>

        {isEmpty(medications) && (
          <Typography variant="body1" textAlign={'center'}>
            {t('noMedicationsFound')}
          </Typography>
        )}

        {!isEmpty(medications) &&
          medications.map((item, index) => (
            <MedicationCard
              key={index}
              name={item?.name || ''}
              frequency={item?.frequency === 1 ? 'Daily' : `Weekly (${item?.weekDayFrequency?.map((item) => item)})`}
              dose={`${item?.quantity || item?.timeFrequency[0]?.quantity} ${item?.type} / day`}
              times={map(item?.timeFrequency, (item) => item?.time)}
              howToTake={t(item.howToTake) || 'After meal'}
              description={item?.description || ''}
              responsible={item?.responsible}
              duration={`${item?.duration || 2} days`}
              handleDeleteMedications={() => handleDeleteMedications(item?.id)}
              color={item?.color}
            />
          ))}
      </Box>
      <SmartMedCheckModal
        open={showSmartMedModal}
        onClose={closeSmartMedModal}
        analysisData={analysisData}
        isLoading={isAnalyzing}
      />
    </>
  );
};

export default MedicationsList;
